import { useEffect, useState } from 'preact/hooks';
import { selectedProperty, setSelectedProperty, startDate, endDate } from '../state';
import { api } from '../api';
import { buildDashboardUrl } from '../state';

interface Session {
    id: string;
    property_id: string;
    ip: string;
    country_code: string;
    browser: string;
    screen_size: string;
    operating_system: string;
    date_created: string;
    referrer?: string;
    duration?: number;
}

type SortField = 'date_created' | 'duration' | 'ip' | 'country_code' | 'browser' | 'operating_system' | 'screen_size';
type SortOrder = 'asc' | 'desc';

interface FilterValues {
    browser: string[];
    country_code: string[];
    operating_system: string[];
    screen_size: string[];
    goal_id: Array<{ id: string; label: string }>;
}

interface ActiveFilters {
    [key: string]: string[];
}

const normalizeLeadIds = (values: string[]) =>
    [...new Set(
        values
            .flatMap(value => value.split(','))
            .map(value => value.trim())
            .filter(Boolean)
    )];

const readLeadIdsFromUrl = () => {
    const url = new URL(window.location.href);
    return normalizeLeadIds([
        ...url.searchParams.getAll('lead_id'),
        ...url.searchParams.getAll('lead')
    ]);
};

const formatLeadIdsForInput = (leadIds: string[]) => leadIds.join(', ');

const Sessions = () => {
    const [sessions, setSessions] = useState<Session[]>([]);
    const [loading, setLoading] = useState(true);
    const [sortField, setSortField] = useState<SortField>('date_created');
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(25);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [filterValues, setFilterValues] = useState<FilterValues>({
        browser: [],
        country_code: [],
        operating_system: [],
        screen_size: [],
        goal_id: []
    });
    const [activeFilters, setActiveFilters] = useState<ActiveFilters>({});
    const [showFilterModal, setShowFilterModal] = useState(false);
    const [selectedFilterField, setSelectedFilterField] = useState<keyof FilterValues | ''>('');
    const [leadIdFilters, setLeadIdFilters] = useState(readLeadIdsFromUrl);
    const [leadIdInput, setLeadIdInput] = useState(() => formatLeadIdsForInput(readLeadIdsFromUrl()));
    const [resolvingLeadProperty, setResolvingLeadProperty] = useState(!selectedProperty.value && readLeadIdsFromUrl().length > 0);

    useEffect(() => {
        const syncLeadIdsFromUrl = () => {
            const urlLeadIds = readLeadIdsFromUrl();
            setLeadIdFilters(urlLeadIds);
            setLeadIdInput(formatLeadIdsForInput(urlLeadIds));
            setResolvingLeadProperty(!selectedProperty.value && urlLeadIds.length > 0);
            setCurrentPage(1);
        };

        syncLeadIdsFromUrl();
        window.addEventListener('popstate', syncLeadIdsFromUrl);

        return () => {
            window.removeEventListener('popstate', syncLeadIdsFromUrl);
        };
    }, []);

    useEffect(() => {
        if (selectedProperty.value || leadIdFilters.length === 0) {
            setResolvingLeadProperty(false);
            return;
        }

        let cancelled = false;

        const resolveLeadProperty = async () => {
            setResolvingLeadProperty(true);
            try {
                const data = await api(`/api/leads/${leadIdFilters[0]}/property`, {
                    displayErrorModal: false,
                });
                if (!cancelled && data?.property) {
                    setSelectedProperty(data.property, { updateUrl: false });
                }
            } catch (error) {
                console.error('Error resolving lead property:', error);
            } finally {
                if (!cancelled) {
                    setResolvingLeadProperty(false);
                }
            }
        };

        void resolveLeadProperty();

        return () => {
            cancelled = true;
        };
    }, [leadIdFilters, selectedProperty.value]);

    useEffect(() => {
        if (selectedProperty.value && !resolvingLeadProperty) {
            fetchSessions();
            fetchFilterValues();
        }
    }, [selectedProperty.value, startDate.value, endDate.value, resolvingLeadProperty]);

    useEffect(() => {
        if (selectedProperty.value && !resolvingLeadProperty) {
            fetchSessions();
        }
    }, [currentPage, itemsPerPage, sortField, sortOrder, activeFilters, leadIdFilters, resolvingLeadProperty]);

    const fetchSessions = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({
                property: selectedProperty.value || '',
                start: startDate.value || '',
                end: endDate.value || '',
                page: currentPage.toString(),
                limit: itemsPerPage.toString(),
                sortField,
                sortOrder
            });

            leadIdFilters.forEach(leadId => params.append('lead_id', leadId));

            if (Object.keys(activeFilters).length > 0) {
                params.append('filters', JSON.stringify(activeFilters));
            }

            const data = await api(`/api/sessions?${params}`);
            setSessions(data.data || []);
            setTotalPages(data.pagination.totalPages);
            setTotalRecords(data.pagination.total);
        } catch (error) {
            console.error('Error fetching sessions:', error);
            setSessions([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchFilterValues = async () => {
        try {
            const params = new URLSearchParams({
                property: selectedProperty.value || '',
                start: startDate.value || '',
                end: endDate.value || ''
            });

            const data = await api(`/api/sessions/filters?${params}`);
            setFilterValues(data);
        } catch (error) {
            console.error('Error fetching filter values:', error);
        }
    };

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('desc');
        }
        setCurrentPage(1);
    };

    const getSortIcon = (field: SortField) => {
        if (sortField !== field) return '↕️';
        return sortOrder === 'asc' ? '↑' : '↓';
    };

    const goToPage = (page: number) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)));
    };

    const updateLeadIdsInUrl = (values: string[]) => {
        const url = new URL(window.location.href);
        url.searchParams.delete('lead_id');
        url.searchParams.delete('lead');

        values.forEach(value => {
            url.searchParams.append('lead_id', value);
        });

        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    };

    const applyLeadIdFilter = () => {
        const nextLeadIds = normalizeLeadIds([leadIdInput]);
        setLeadIdFilters(nextLeadIds);
        setLeadIdInput(formatLeadIdsForInput(nextLeadIds));
        updateLeadIdsInUrl(nextLeadIds);
        setCurrentPage(1);
    };

    const clearLeadIdFilter = () => {
        setLeadIdFilters([]);
        setLeadIdInput('');
        updateLeadIdsInUrl([]);
        setCurrentPage(1);
    };

    const removeLeadIdFilter = (leadIdToRemove: string) => {
        const nextLeadIds = leadIdFilters.filter(leadId => leadId !== leadIdToRemove);
        setLeadIdFilters(nextLeadIds);
        setLeadIdInput(formatLeadIdsForInput(nextLeadIds));
        updateLeadIdsInUrl(nextLeadIds);
        setCurrentPage(1);
    };

    const addFilter = (field: keyof FilterValues, value: string) => {
        setActiveFilters(prev => {
            const current = prev[field] || [];
            if (current.includes(value)) return prev;
            return { ...prev, [field]: [...current, value] };
        });
        setCurrentPage(1);
        setShowFilterModal(false);
        setSelectedFilterField('');
    };

    const removeFilter = (field: string, value: string) => {
        setActiveFilters(prev => {
            const current = prev[field] || [];
            const updated = current.filter(v => v !== value);
            if (updated.length === 0) {
                const { [field]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [field]: updated };
        });
        setCurrentPage(1);
    };

    const getFieldLabel = (field: string) => {
        const labels: Record<string, string> = {
            browser: 'Browser',
            country_code: 'Paese',
            operating_system: 'Sistema Operativo',
            screen_size: 'Dimensione Schermo',
            goal_id: 'Conversione'
        };
        return labels[field] || field;
    };

    const getGoalLabel = (goalId: string) => {
        const goal = filterValues.goal_id.find(g => g.id === goalId);
        return goal ? goal.label : goalId;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString();
    };

    const formatDuration = (seconds: number | undefined) => {
        if (!seconds) return '-';
        
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        
        const parts = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);
        if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
        
        return parts.join(' ');
    };

    const truncateText = (text: string, maxLength: number = 50) => {
        if (!text) return '-';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    if (resolvingLeadProperty) {
        return (
            <div className="dashboard-container">
                <div className="card">
                    <p className="no-data-message">Caricamento sessioni del lead...</p>
                </div>
            </div>
        );
    }

    if (!selectedProperty.value) {
        return (
            <div className="dashboard-container">
                <div className="card">
                    <p className="no-data-message">Seleziona una proprietà per visualizzare le sessioni.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard-container">
            <div className="card">
                <div className="card-header">
                    <div>
                        <h2 className="card-title">Sessioni</h2>
                        <p className="card-subtitle">
                            {totalRecords} session{totalRecords !== 1 ? 'i' : 'e'} trovat{totalRecords !== 1 ? 'e' : 'a'}
                        </p>
                    </div>
                </div>

                <div className="table-controls">
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                            type="text"
                            value={leadIdInput}
                            onInput={(e) => setLeadIdInput((e.target as HTMLInputElement).value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    applyLeadIdFilter();
                                }
                            }}
                            placeholder="Filtra per uno o più lead_id"
                            className="page-size-select"
                            style={{ minWidth: '320px' }}
                        />
                        <button
                            onClick={applyLeadIdFilter}
                            className="pagination-btn"
                            style={{ padding: '8px 16px' }}
                        >
                            Filtra lead_id
                        </button>
                        {leadIdFilters.length > 0 && (
                            <button
                                onClick={clearLeadIdFilter}
                                className="pagination-btn"
                                style={{ padding: '8px 16px' }}
                            >
                                Reset lead_id
                            </button>
                        )}
                        <button 
                            onClick={() => setShowFilterModal(true)} 
                            className="pagination-btn"
                            style={{ padding: '8px 16px' }}
                        >
                            + Aggiungi Filtro
                        </button>
                        
                        {Object.entries(activeFilters).map(([field, values]) => 
                            values.map(value => (
                                <div 
                                    key={`${field}-${value}`}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '4px 12px',
                                        background: '#e0e7ff',
                                        borderRadius: '16px',
                                        fontSize: '14px'
                                    }}
                                >
                                    <span style={{ color: '#4f46e5', fontWeight: '500' }}>
                                        {getFieldLabel(field)}:
                                    </span>
                                    <span>{field === 'goal_id' ? getGoalLabel(value) : value}</span>
                                    <button
                                        onClick={() => removeFilter(field, value)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '0 4px',
                                            fontSize: '16px',
                                            color: '#4f46e5'
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))
                        )}
                        {leadIdFilters.map(leadId => (
                            <div
                                key={leadId}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '4px 12px',
                                    background: '#e0f2fe',
                                    borderRadius: '16px',
                                    fontSize: '14px'
                                }}
                            >
                                <span style={{ color: '#0369a1', fontWeight: '500' }}>
                                    lead_id:
                                </span>
                                <span>{leadId}</span>
                                <button
                                    onClick={() => removeLeadIdFilter(leadId)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        padding: '0 4px',
                                        fontSize: '16px',
                                        color: '#0369a1'
                                    }}
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                    <div className="items-per-page">
                        <label>
                            Mostra:
                            <select
                                value={itemsPerPage}
                                onChange={(e) => {
                                    setItemsPerPage(Number((e.target as HTMLSelectElement).value));
                                    setCurrentPage(1);
                                }}
                                className="page-size-select"
                            >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                            </select>
                        </label>
                    </div>
                </div>

                {loading ? (
                    <div className="loading-state">Caricamento sessioni...</div>
                ) : sessions.length === 0 ? (
                    <div className="no-data-message">Nessuna sessione trovata.</div>
                ) : (
                    <>
                        <div className="table-wrapper">
                            <table className="data-table sessions-table">
                                <thead>
                                    <tr>
                                        <th onClick={() => handleSort('date_created')} className="sortable">
                                            Data {getSortIcon('date_created')}
                                        </th>
                                        <th>ID Sessione</th>
                                        <th onClick={() => handleSort('ip')} className="sortable">
                                            Indirizzo IP {getSortIcon('ip')}
                                        </th>
                                        <th onClick={() => handleSort('country_code')} className="sortable">
                                            Paese {getSortIcon('country_code')}
                                        </th>
                                        <th onClick={() => handleSort('browser')} className="sortable">
                                            Browser {getSortIcon('browser')}
                                        </th>
                                        <th onClick={() => handleSort('operating_system')} className="sortable">
                                            SO {getSortIcon('operating_system')}
                                        </th>
                                        <th onClick={() => handleSort('screen_size')} className="sortable">
                                            Dimensione Schermo {getSortIcon('screen_size')}
                                        </th>
                                        <th>Referrer</th>
                                        <th onClick={() => handleSort('duration')} className="sortable">
                                            Durata {getSortIcon('duration')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sessions.map((session: Session) => (
                                        <tr key={session.id}>
                                            <td className="table-cell-primary">
                                                {formatDate(session.date_created)}
                                            </td>
                                            <td className="table-cell-secondary">
                                                <a href={buildDashboardUrl(`/dashboard/sessions/${session.id}`, { preserveCurrentSearch: true })} className="session-link" title={session.id}>
                                                    {truncateText(session.id, 30)}
                                                </a>
                                            </td>
                                            <td>{session.ip ? session.ip.replace(/\.\d+$/, '.xxx') : '-'}</td>
                                            <td>{session.country_code || '-'}</td>
                                            <td>{session.browser || '-'}</td>
                                            <td>{session.operating_system || '-'}</td>
                                            <td>{session.screen_size || '-'}</td>
                                            <td>
                                                <span title={session.referrer}>
                                                    {truncateText(session.referrer || '', 30)}
                                                </span>
                                            </td>
                                            <td>{formatDuration(session.duration)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="pagination">
                                <button
                                    onClick={() => goToPage(1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    ««
                                </button>
                                <button
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="pagination-btn"
                                >
                                    ‹
                                </button>
                                <span className="pagination-info">
                                    Pagina {currentPage} di {totalPages}
                                </span>
                                <button
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    ›
                                </button>
                                <button
                                    onClick={() => goToPage(totalPages)}
                                    disabled={currentPage === totalPages}
                                    className="pagination-btn"
                                >
                                    »»
                                </button>
                            </div>
                        )}
                    </>
                )}

                {showFilterModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            background: 'white',
                            padding: '24px',
                            borderRadius: '8px',
                            minWidth: '400px',
                            maxWidth: '90%',
                            maxHeight: '80vh',
                            overflow: 'auto'
                        }}>
                            <h3 style={{ marginTop: 0 }}>Aggiungi Filtro</h3>
                            
                            {!selectedFilterField ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {(Object.keys(filterValues) as Array<keyof FilterValues>).map(field => (
                                        <button
                                            key={field}
                                            onClick={() => setSelectedFilterField(field)}
                                            className="pagination-btn"
                                            style={{ padding: '12px', textAlign: 'left' }}
                                        >
                                            {getFieldLabel(field)}
                                        </button>
                                    ))}
                                </div>
                            ) : (
                                <>
                                    <div style={{ marginBottom: '16px' }}>
                                        <button
                                            onClick={() => setSelectedFilterField('')}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: '#4f46e5',
                                                padding: '4px 8px'
                                            }}
                                        >
                                            ← Indietro
                                        </button>
                                        <h4 style={{ margin: '8px 0' }}>{getFieldLabel(selectedFilterField)}</h4>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {selectedFilterField === 'goal_id' ? (
                                            filterValues.goal_id
                                                .filter(goal => !activeFilters.goal_id?.includes(goal.id))
                                                .map(goal => (
                                                    <button
                                                        key={goal.id}
                                                        onClick={() => addFilter('goal_id', goal.id)}
                                                        className="pagination-btn"
                                                        style={{ padding: '12px', textAlign: 'left' }}
                                                    >
                                                        {goal.label}
                                                    </button>
                                                ))
                                        ) : (
                                            (filterValues[selectedFilterField] as string[])
                                                .filter(value => !activeFilters[selectedFilterField]?.includes(value))
                                                .map(value => (
                                                    <button
                                                        key={value}
                                                        onClick={() => addFilter(selectedFilterField, value)}
                                                        className="pagination-btn"
                                                        style={{ padding: '12px', textAlign: 'left' }}
                                                    >
                                                        {value}
                                                    </button>
                                                ))
                                        )}
                                        {(selectedFilterField === 'goal_id' 
                                            ? filterValues.goal_id.filter(g => !activeFilters.goal_id?.includes(g.id)).length === 0
                                            : (filterValues[selectedFilterField] as string[]).filter(value => !activeFilters[selectedFilterField]?.includes(value)).length === 0
                                        ) && (
                                            <p style={{ color: '#666', textAlign: 'center' }}>Nessun altro valore disponibile</p>
                                        )}
                                    </div>
                                </>
                            )}
                            
                            <button
                                onClick={() => {
                                    setShowFilterModal(false);
                                    setSelectedFilterField('');
                                }}
                                className="pagination-btn"
                                style={{ marginTop: '16px', width: '100%', padding: '12px' }}
                            >
                                Chiudi
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Sessions;
